import React, { useState, useEffect, useMemo } from "react";
import { Card, Row, Col, Badge, Button, ButtonGroup, Modal, Spinner, ProgressBar, Table } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getPartnerDetail, getPartnerHost, updatePartner, updatePartnerWhatsappLimit, getHostCreatedGraph } from "../../services/UserServices/UserService";
import { PartnerPlansTable, PartnerPlanHistoryTable } from "../components/PartnerDetailComponents";
import Pagination from "../components/Pagination";
import PageTitle from "../layouts/PageTitle";
import { getImageUrl } from "../../utils/imageUtils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Edit3,
  ArrowLeft,
  MapPin,
  Building2,
  Calendar,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "N/A";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const PERIODS = [
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "yearly", label: "Yearly" },
];

// WhatsApp messages sent
const WA_MONTHLY = [
  { period: "Jan", messages: 1200 },
  { period: "Feb", messages: 980 },
  { period: "Mar", messages: 2100 },
  { period: "Apr", messages: 1750 },
  { period: "May", messages: 2600 },
  { period: "Jun", messages: 2300 },
];

const WA_QUARTERLY = [
  { period: "Q1", messages: 4280 },
  { period: "Q2", messages: 6650 },
];

const WA_YEARLY = [
  { period: "2023", messages: 18400 },
  { period: "2024", messages: 21090 },
];

// Weddings created total per period
const WEDDINGS_TOTAL_MONTHLY = [
  { period: "Jan", weddings: 7 },
  { period: "Feb", weddings: 6 },
  { period: "Mar", weddings: 11 },
  { period: "Apr", weddings: 12 },
  { period: "May", weddings: 15 },
  { period: "Jun", weddings: 17 },
];

const WEDDINGS_TOTAL_QUARTERLY = [
  { period: "Q1", weddings: 24 },
  { period: "Q2", weddings: 44 },
];

const WEDDINGS_TOTAL_YEARLY = [
  { period: "2023", weddings: 60 },
  { period: "2024", weddings: 68 },
];

const PACKAGE_BAR_COLORS = ["#A60000", "#800001", "#059669", "#2563eb", "#ea580c", "#ec4899", "#7c3aed"];

// Weddings created per plan type per month
const WEDDINGS_BY_TYPE_MONTHLY = [
  { month: "Jan", Essential: 4, Premium: 8, Elite: 3 },
  { month: "Feb", Essential: 6, Premium: 10, Elite: 5 },
  { month: "Mar", Essential: 8, Premium: 14, Elite: 7 },
  { month: "Apr", Essential: 5, Premium: 11, Elite: 4 },
  { month: "May", Essential: 10, Premium: 16, Elite: 9 },
  { month: "Jun", Essential: 7, Premium: 12, Elite: 6 },
];

const getDateRangeForPeriod = (period) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  const formatDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  if (period === "weekly") {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return { startDate: formatDateStr(start), endDate: formatDateStr(now) };
  }

  if (period === "monthly") {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return { startDate: formatDateStr(start), endDate: formatDateStr(end) };
  }

  if (period === "yearly") {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    return { startDate: formatDateStr(start), endDate: formatDateStr(end) };
  }

  return { startDate: "", endDate: "" };
};



const PeriodSwitcher = ({ value, onChange }) => (
  <ButtonGroup size="sm">
    {PERIODS.map((p) => (
      <Button
        key={p.key}
        variant={value === p.key ? "dark" : "outline-secondary"}
        style={{
          fontSize: "0.72rem",
          padding: "2px 8px",
          backgroundColor: value === p.key ? "#800001" : "transparent",
          borderColor: value === p.key ? "#800001" : "#ced4da",
          color: value === p.key ? "#fff" : "#6c757d",
        }}
        onClick={() => onChange(p.key)}
      >
        {p.label}
      </Button>
    ))}
  </ButtonGroup>
);

const PartnerDetail = ({ partner: propPartner }) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [partnerData, setPartnerData] = useState(
    location.state?.partner || propPartner || null
  );
  const [selectedImage, setSelectedImage] = useState(null);
  const [hostCreatedPeriod, setHostCreatedPeriod] = useState("monthly");
  const initialHostCreatedDates = getDateRangeForPeriod("monthly");
  const [chartStartDate, setChartStartDate] = useState(initialHostCreatedDates.startDate);
  const [chartEndDate, setChartEndDate] = useState(initialHostCreatedDates.endDate);
  const [hostCreatedGraph, setHostCreatedGraph] = useState([]);

  const handleHostCreatedPeriodChange = (e) => {
    const selected = e.target.value;
    setHostCreatedPeriod(selected);
    if (selected) {
      const dates = getDateRangeForPeriod(selected);
      setChartStartDate(dates.startDate);
      setChartEndDate(dates.endDate);
    }
  };

  const handleChartStartDateChange = (e) => {
    setChartStartDate(e.target.value);
    setHostCreatedPeriod("");
  };

  const handleChartEndDateChange = (e) => {
    setChartEndDate(e.target.value);
    setHostCreatedPeriod("");
  };

  const handleResetChartFilters = () => {
    setHostCreatedPeriod("monthly");
    const dates = getDateRangeForPeriod("monthly");
    setChartStartDate(dates.startDate);
    setChartEndDate(dates.endDate);
  };

  useEffect(() => {
    const fetchHostCreatedData = async () => {
      const partnerId = id || location.state?.partner?._id || location.state?.partner?.id;
      if (!partnerId || !chartStartDate || !chartEndDate) return;
      try {
        const res = await getHostCreatedGraph(partnerId, chartStartDate, chartEndDate);
        console.log("host created graph res", res);
        const dataObj = res?.data?.data || res?.data;
        const graphArray = dataObj?.graph || dataObj?.data?.graph || (Array.isArray(dataObj) ? dataObj : []);
        setHostCreatedGraph(Array.isArray(graphArray) ? graphArray : []);
      } catch (err) {
        console.error("Error fetching host created graph:", err);
      }
    };
    fetchHostCreatedData();
  }, [id, location.state, chartStartDate, chartEndDate]);

  const weddingsCreatedChartData = React.useMemo(() => {
    if (!hostCreatedGraph || hostCreatedGraph.length === 0) {
      return [];
    }
    return hostCreatedGraph.map((item) => {
      const obj = { ...item };
      obj.period = item.period || item.month || item.name || "";
      return obj;
    });
  }, [hostCreatedGraph]);

  const dynamicHostCreatedKeys = React.useMemo(() => {
    if (!weddingsCreatedChartData || weddingsCreatedChartData.length === 0) return [];
    const keysSet = new Set();
    weddingsCreatedChartData.forEach((item) => {
      Object.keys(item).forEach((k) => {
        if (!["period", "_id", "id", "granularity", "startDate", "endDate"].includes(k)) {
          keysSet.add(k);
        }
      });
    });
    return Array.from(keysSet);
  }, [weddingsCreatedChartData]);

  const fetchDetail = async () => {
    const partnerId = id || location.state?.partner?._id || location.state?.partner?.id;
    if (!partnerId) return;
    try {
      setLoading(true);
      const res = await getPartnerDetail(partnerId);
      const data = res?.data?.data || res?.data;
      if (data) {
        setPartnerData(data);
      }
    } catch (err) {
      console.error("Error fetching partner details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const [showWeddingsModal, setShowWeddingsModal] = useState(false);

  const [waPeriod, setWaPeriod] = useState("monthly");
  const [totalWeddingsPeriod, setTotalWeddingsPeriod] = useState("monthly");

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [newLimitValue, setNewLimitValue] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  const handleOpenLimitModal = () => {
    const currentVal = partnerData?.plan?.whatsappMessageLimit ?? 0;
    setNewLimitValue(String(currentVal));
    setShowLimitModal(true);
  };

  const handleSaveMessageLimit = async () => {
    const partnerId = id || location.state?.partner?._id || location.state?.partner?.id;
    if (!partnerId) {
      toast.error("Partner ID not found");
      return;
    }
    if (newLimitValue === "" || isNaN(Number(newLimitValue))) {
      toast.error("Please enter a valid message limit");
      return;
    }
    const numLimit = Number(newLimitValue);
    const existingLimit = partnerData?.plan?.whatsappMessageLimit ?? 0;
    if (numLimit < existingLimit) {
      toast.error(`New message limit cannot be less than the existing limit (${existingLimit})`);
      return;
    }

    try {
      setSavingLimit(true);
      await updatePartnerWhatsappLimit({
        partnerId,
        whatsappMessageLimit: numLimit,
      });
      toast.success("Message limit updated successfully!");
      setPartnerData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          hostLimit: numLimit,
          remainingHosts: Math.max(0, numLimit - (prev?.hostCount || 0)),
          plan: {
            ...(prev?.plan || {}),
            whatsappMessageLimit: numLimit,
          },
        };
      });
      setShowLimitModal(false);
      fetchDetail();
    } catch (err) {
      console.error("Error updating message limit:", err);
      toast.error(err?.response?.data?.message || "Failed to update message limit");
    } finally {
      setSavingLimit(false);
    }
  };

  const [hostsPreview, setHostsPreview] = useState([]);
  const [loadingHosts, setLoadingHosts] = useState(false);

  const [modalHosts, setModalHosts] = useState([]);
  const [loadingModalHosts, setLoadingModalHosts] = useState(false);
  const [modalPage, setModalPage] = useState(0);
  const [modalTotalHosts, setModalTotalHosts] = useState(0);
  const [modalTotalPages, setModalTotalPages] = useState(1);

  const fetchHostsPreview = async (partnerId) => {
    if (!partnerId) return;
    try {
      setLoadingHosts(true);
      const res = await getPartnerHost(partnerId, 1, 5);
      const resData = res?.data?.data || res?.data;
      const list = resData?.hosts || resData?.docs || resData?.list || (Array.isArray(resData) ? resData : []);
      const total =
        res?.data?.count ??
        resData?.count ??
        res?.data?.totalCount ??
        resData?.totalCount ??
        res?.data?.total ??
        resData?.total ??
        res?.data?.totalHosts ??
        resData?.totalHosts ??
        (Array.isArray(list) ? list.length : 0);
      setHostsPreview(Array.isArray(list) ? list : []);
      setModalTotalHosts(total);
    } catch (err) {
      console.error("Error fetching partner hosts preview:", err);
    } finally {
      setLoadingHosts(false);
    }
  };

  const fetchModalHosts = async (partnerId, pageIndex) => {
    if (!partnerId) return;
    try {
      setLoadingModalHosts(true);
      const limit = 10;
      const res = await getPartnerHost(partnerId, pageIndex + 1, limit);
      const resData = res?.data?.data || res?.data;
      const list = resData?.hosts || resData?.docs || resData?.list || (Array.isArray(resData) ? resData : []);
      const total =
        res?.data?.count ??
        resData?.count ??
        res?.data?.totalCount ??
        resData?.totalCount ??
        res?.data?.total ??
        resData?.total ??
        res?.data?.totalHosts ??
        resData?.totalHosts ??
        (Array.isArray(list) ? list.length : 0);
      const pages = resData?.totalPages ?? (Math.ceil(total / limit) || 1);
      setModalHosts(Array.isArray(list) ? list : []);
      setModalTotalHosts(total);
      setModalTotalPages(pages);
    } catch (err) {
      console.error("Error fetching modal partner hosts:", err);
    } finally {
      setLoadingModalHosts(false);
    }
  };

  useEffect(() => {
    const partnerId = id || location.state?.partner?._id || location.state?.partner?.id;
    if (partnerId) {
      fetchHostsPreview(partnerId);
    }
  }, [id, location.state]);

  useEffect(() => {
    const partnerId = id || location.state?.partner?._id || location.state?.partner?.id;
    if (showWeddingsModal && partnerId) {
      fetchModalHosts(partnerId, modalPage);
    }
  }, [showWeddingsModal, modalPage, id, location.state]);



  const waData = useMemo(() => {
    if (waPeriod === "quarterly") return WA_QUARTERLY;
    if (waPeriod === "yearly") return WA_YEARLY;
    return WA_MONTHLY;
  }, [waPeriod]);

  const totalWeddingsData = useMemo(() => {
    if (totalWeddingsPeriod === "quarterly") return WEDDINGS_TOTAL_QUARTERLY;
    if (totalWeddingsPeriod === "yearly") return WEDDINGS_TOTAL_YEARLY;
    return WEDDINGS_TOTAL_MONTHLY;
  }, [totalWeddingsPeriod]);



  const currentPartner = partnerData || {};
  const pData = currentPartner?.partnerData || {};

  const hostName = currentPartner?.hostName || currentPartner?.name || currentPartner?.ownerName || "Partner Details";
  const email = pData?.email || currentPartner?.email || "-";
  const companyName = pData?.companyName || currentPartner?.companyName || "";
  const phone = currentPartner?.phoneNumber
    ? `${currentPartner?.countryCode || "+91"} ${currentPartner?.phoneNumber}`
    : currentPartner?.phone || "-";
  const city = pData?.city || currentPartner?.city || "";
  const gstNumber = pData?.gstNumber || currentPartner?.gstNumber || "";
  const aquisitionSource = pData?.aquisitionSource || pData?.acquisitionSource || currentPartner?.aquisitionSource || currentPartner?.acquisitionSource || "";
  const rawBusinessType = pData?.businessType || currentPartner?.businessType || "";
  const BUSINESS_TYPE_LABELS = {
    wedding_planner: "Wedding Planner",
    event_management: "Event Management Company",
    venue: "Venue / Banquet / Farmhouse",
    decorator: "Decorator / Production Partner",
    invitation: "Invitation / Creative Agency",
    hospitality: "Hospitality / Travel Partner",
  };
  const businessType = BUSINESS_TYPE_LABELS[rawBusinessType] || rawBusinessType;
  const alternatePhone = currentPartner?.alternatePhoneNumber || pData?.alternatePhoneNumber || "";
  const website = pData?.website || currentPartner?.website || "";
  const instagram = pData?.instagram || currentPartner?.instagram || "";
  const packageName = currentPartner?.plan?.name || pData?.packageName || currentPartner?.packageName || "";
  const isBlocked = currentPartner?.isBlocked;
  const dateConnected = currentPartner?.createdAt;

  const logo = pData?.logo || currentPartner?.logo || "";
  const rawPortfolio = pData?.portfolioImages || currentPartner?.portfolioImages || pData?.images || currentPartner?.images;
  const portfolioImages = Array.isArray(rawPortfolio)
    ? rawPortfolio
    : rawPortfolio
      ? [rawPortfolio]
      : [];

  return (
    <div className="qx-page qx-detail-page qx-partner-detail">
      <PageTitle
        pageTitle="Partner Profile"
        subtitle="View & manage partner details, stats, and media portfolio"
        breadcrumbs={[
          { label: "Partners", path: "/partners" },
          { label: hostName || "Partner" },
        ]}
        actions={
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outline-secondary"
              className="d-inline-flex align-items-center gap-2"
              onClick={() => navigate("/partners")}
            >
              <ArrowLeft size={16} />
              Back
            </Button>
            <Button
              className="btn-primary qx-btn-brand d-inline-flex align-items-center gap-2"
              onClick={() =>
                navigate("/edit-partner", {
                  state: {
                    partner: currentPartner,
                    activeTab: location.state?.activeTab,
                    currentPage: location.state?.currentPage,
                  },
                })
              }
            >
              <Edit3 size={16} />
              Edit Partner Profile
            </Button>
          </div>
        }
      />

      <Card className="premium-card qx-section-card mb-4 overflow-hidden">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex flex-column flex-md-row align-items-md-center gap-3 gap-md-4">
            <div className="qx-partner-logo flex-shrink-0">
              {logo ? (
                <img src={getImageUrl(logo)} alt="Company Logo" />
              ) : (
                <Building2 size={36} />
              )}
            </div>

            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                <h4 className="fw-bold mb-0 text-dark">{hostName}</h4>
                {loading && <Spinner animation="border" size="sm" style={{ color: "#A60000" }} />}
              </div>
              {companyName && (
                <p className="text-muted mb-2 d-flex align-items-center gap-2">
                  <Building2 size={16} />
                  {companyName}
                </p>
              )}

              <div className="d-flex align-items-center gap-2 flex-wrap">
                {packageName && (
                  <Badge
                    className="px-3 py-2 fw-semibold"
                    style={{ borderRadius: "0.5rem", background: "#800001" }}
                  >
                    Plan: {packageName}
                  </Badge>
                )}
              
                {isBlocked  ? (
                  <Badge bg="danger" className="px-3 py-2 fw-semibold" style={{ borderRadius: "0.5rem" }}>
                    Blocked Partner
                  </Badge>
                ) : isBlocked === false ? (
                  <Badge
                    bg="success"
                    className="px-3 py-2 fw-semibold d-inline-flex align-items-center gap-1"
                    style={{ borderRadius: "0.5rem", backgroundColor: "#059669" }}
                  >
                    <ShieldCheck size={14} /> Active Partner
                  </Badge>
                ):
                (  <Badge
                    bg="danger"
                    className="px-3 py-2 fw-semibold d-inline-flex align-items-center gap-1"
                    style={{ borderRadius: "0.5rem", backgroundColor: "#059669" }}
                  >
                    <ShieldCheck size={14} /> Deleted Partner
                  </Badge>)
                }
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="premium-card qx-section-card mb-4">
        <Card.Body className="p-3 p-md-4">
          <h6 className="qx-section-card__title text-uppercase small mb-3">
            Partner Information
          </h6>
          <div className="qx-host-info-grid">
            <div className="qx-detail-field">
              <span className="qx-detail-field__label">Owner / Host</span>
              <span className="qx-detail-field__value">{hostName || "—"}</span>
            </div>
            <div className="qx-detail-field">
              <span className="qx-detail-field__label">Email Address</span>
              <span className="qx-detail-field__value">{email || "—"}</span>
            </div>
            <div className="qx-detail-field">
              <span className="qx-detail-field__label">Phone Contact</span>
              <span className="qx-detail-field__value">{phone || "—"}</span>
            </div>
            {alternatePhone && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">Alternate Phone</span>
                <span className="qx-detail-field__value">{alternatePhone}</span>
              </div>
            )}
            {businessType && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">Business Type</span>
                <span className="qx-detail-field__value">{businessType}</span>
              </div>
            )}
            {companyName && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">Company Name</span>
                <span className="qx-detail-field__value">{companyName}</span>
              </div>
            )}
            {gstNumber && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">GST Number</span>
                <span className="qx-detail-field__value">{gstNumber}</span>
              </div>
            )}
            {aquisitionSource && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">Acquisition Source</span>
                <span className="qx-detail-field__value text-capitalize">{aquisitionSource}</span>
              </div>
            )}
            {website && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">Website</span>
                <a
                  href={website.startsWith("http") ? website : `https://${website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="qx-detail-field__value text-decoration-none"
                  style={{ color: "#A60000" }}
                >
                  {website}
                </a>
              </div>
            )}
            {instagram && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">Instagram</span>
                <a
                  href={instagram.startsWith("http") ? instagram : `https://instagram.com/${instagram.replace(/^@/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="qx-detail-field__value text-decoration-none"
                  style={{ color: "#A60000" }}
                >
                  {instagram}
                </a>
              </div>
            )}
            {dateConnected && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">Date Joined</span>
                <span className="qx-detail-field__value">{formatDate(dateConnected)}</span>
              </div>
            )}
            {city && (
              <div className="qx-detail-field">
                <span className="qx-detail-field__label">City</span>
                <span className="qx-detail-field__value">{city}</span>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="premium-card qx-section-card mb-4">
        <Card.Body className="p-3 p-md-4">
          <h6 className="qx-section-card__title text-uppercase small mb-3">
            Wedding Usage
          </h6>
          <div className="qx-partner-stats-grid">
            <div className="qx-partner-stat">
              <span className="qx-partner-stat__label">Wedding Limit</span>
              <span className="qx-partner-stat__value qx-partner-stat__value--brand">
                {partnerData?.hostLimit ?? 0}
              </span>
            </div>
            <div className="qx-partner-stat">
              <span className="qx-partner-stat__label">Wedding Created</span>
              <span className="qx-partner-stat__value">{partnerData?.hostCount ?? 0}</span>
            </div>
            <div className="qx-partner-stat">
              <span className="qx-partner-stat__label">Remaining Wedding</span>
              <span className="qx-partner-stat__value">{partnerData?.remainingHosts ?? 0}</span>
            </div>
            <div className="qx-partner-stat">
              <span className="qx-partner-stat__label">Active Wedding</span>
              <span className="qx-partner-stat__value">{partnerData?.activeWeddings ?? 0}</span>
            </div>
            <div className="qx-partner-stat">
              <span className="qx-partner-stat__label">Completed Wedding</span>
              <span className="qx-partner-stat__value">{partnerData?.completedWeddings ?? 0}</span>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="premium-card qx-section-card mb-4">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h6 className="qx-section-card__title text-uppercase small mb-1">
                Portfolio Gallery
              </h6>
              <p className="text-muted small mb-0">Click any image to enlarge</p>
            </div>
            {portfolioImages.length > 0 && (
              <Badge bg="light" text="dark" className="border px-3 py-2">
                {portfolioImages.length} {portfolioImages.length === 1 ? "file" : "files"}
              </Badge>
            )}
          </div>

          {portfolioImages.length > 0 ? (
            <Row className="g-3">
              {portfolioImages.map((img, idx) => (
                <Col key={idx} xs={6} sm={4} md={3} lg={2}>
                  <button
                    type="button"
                    className="qx-partner-portfolio-tile"
                    onClick={() => setSelectedImage(getImageUrl(img))}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`Portfolio ${idx + 1}`}
                    />
                  </button>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="qx-empty-state py-4">
              <p className="mb-0 text-muted">No portfolio media uploaded yet for this partner.</p>
            </div>
          )}
        </Card.Body>
      </Card>

      <style>{`
        /* Keep chart card chrome readable without genz cream theme */
        .qx-partner-detail .genz-card-box,
        .qx-partner-detail .genz-stat-card {
          background: #ffffff !important;
          border: 1px solid #e4e7ec !important;
          border-radius: 16px !important;
          box-shadow: none !important;
        }
        .qx-partner-detail .genz-card-box:hover,
        .qx-partner-detail .genz-stat-card:hover {
          transform: none !important;
          border-color: #e4e7ec !important;
          box-shadow: none !important;
        }
        .qx-partner-detail .genz-section-title {
          font-weight: 600;
          color: #344054;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .qx-partner-detail .px-3 py-2 fw-semibold {
          background: #A60000 !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          font-size: 0.82rem !important;
          padding: 0.45rem 0.9rem !important;
          border-radius: 0.5rem !important;
          box-shadow: none !important;
          border: none !important;
        }
        .qx-partner-detail .genz-btn-action {
          background: #A60000 !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          border: none !important;
          box-shadow: none !important;
          border-radius: 8px !important;
          padding: 0.5rem 1rem !important;
        }
        .genz-btn-sm {
          padding: 0.25rem 0.5rem !important;
          font-size: 0.75rem !important;
        }
      `}</style>

      {/* Partner Plans & Limits Table Section */}
      <PartnerPlansTable currentPartner={currentPartner} />

      {/* Partner Plan History Section */}
      <PartnerPlanHistoryTable currentPartner={currentPartner} />

      <Row className="g-3 mb-4">
        <Col lg={12}>
          <Card className="premium-card qx-section-card">
            <Card.Body className="p-3 p-md-4">
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <h6 className="qx-section-card__title text-uppercase small mb-0">
                  Partner Hosts
                </h6>
                <Button
                  className="btn-primary qx-btn-brand d-inline-flex align-items-center gap-1"
                  size="sm"
                  onClick={() => navigate(`/partner-events/${id || currentPartner?._id || currentPartner?.id}`)}
                >
                  View All ({modalTotalHosts}) <ArrowRight size={14} />
                </Button>
              </div>

              {loadingHosts ? (
                <div className="text-center py-4">
                  <Spinner animation="border" style={{ color: "#A60000" }} />
                </div>
              ) : hostsPreview && hostsPreview.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {hostsPreview.slice(0, 5).map((item, index) => {
                    const coupleNames =
                      item.groomName && item.brideName
                        ? `${item.groomName} & ${item.brideName}`
                        : item.hostName || "Wedding Event";
                    const coupleImg = item.couplePhoto ? getImageUrl(item.couplePhoto) : null;
                    const formattedDate = item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      : "-";

                    return (
                      <div
                        key={item._id || item.id || index}
                        className="qx-partner-host-row d-flex align-items-center justify-content-between p-3"
                      >
                        <div className="d-flex align-items-center gap-3 min-w-0">
                          {coupleImg ? (
                            <img
                              src={coupleImg}
                              alt={coupleNames}
                              className="rounded-circle flex-shrink-0 object-fit-cover shadow-sm"
                              style={{ width: 44, height: 44, border: "2px solid #A60000" }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                              style={{ width: 44, height: 44, backgroundColor: "#F8DBA0", color: "#A60000" }}
                            >
                              <Calendar size={22} />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="fw-extrabold text-truncate" style={{ fontSize: "0.92rem", color: "#0f172a" }}>
                              {coupleNames}
                            </div>
                            <div className="fw-bold small" style={{ color: "#475569" }}>
                              {formattedDate} • 👥 {item.totalGuests ?? 0} Guests
                            </div>
                          </div>
                        </div>
                        <Badge className="px-3 py-2 fw-semibold text-capitalize px-3 py-2 flex-shrink-0 ms-2">
                          {item.plan?.name || item.hostSide || "Host"}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-muted"  >No  hosts found</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Weddings Created Per Type / Month Bar Chart */}
      <Card className="premium-card qx-section-card mb-4">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom flex-wrap gap-3" style={{ borderColor: "#e4e7ec" }}>
            <div>
              <h6 className="qx-section-card__title text-uppercase small mb-1">
                Weddings Created Per Type / Month
              </h6>
              <span className="small text-muted">
                Breakdown of weddings created by plan type (Essential, Premium, Elite)
              </span>
            </div>

            {/* Start, End Date & Timeframe Controls */}
            <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
              <div>
                <label className="fw-bold small mb-1 d-block" style={{ color: "#A60000", fontSize: "0.75rem" }}>From</label>
                <input
                  type="date"
                  className="form-control form-control-sm fw-bold"
                  value={chartStartDate}
                  onChange={handleChartStartDateChange}
                  style={{ borderRadius: "0.5rem", borderColor: "#e4e7ec" }}
                />
              </div>
              <div>
                <label className="fw-bold small mb-1 d-block" style={{ color: "#A60000", fontSize: "0.75rem" }}>To</label>
                <input
                  type="date"
                  className="form-control form-control-sm fw-bold"
                  value={chartEndDate}
                  onChange={handleChartEndDateChange}
                  style={{ borderRadius: "0.5rem", borderColor: "#e4e7ec" }}
                />
              </div>
              <div>
                <label className="fw-bold small mb-1 d-block" style={{ color: "#A60000", fontSize: "0.75rem" }}>Timeframe</label>
                <select
                  className="form-select form-select-sm fw-bold"
                  value={hostCreatedPeriod}
                  onChange={handleHostCreatedPeriodChange}
                  style={{ borderRadius: "0.5rem", borderColor: "#e4e7ec", minWidth: "130px", color: "#A60000" }}
                >
                  <option value="">Custom Range</option>
                  <option value="weekly">Weekly (Current Week)</option>
                  <option value="monthly">Monthly (Current Month)</option>
                  <option value="yearly">Yearly (Current Year)</option>
                </select>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                className="align-self-end fw-bold"
                style={{ borderRadius: "0.5rem", height: "31px", fontSize: "0.75rem" }}
                onClick={handleResetChartFilters}
              >
                Reset
              </Button>
            </div>
          </div>

          {weddingsCreatedChartData && weddingsCreatedChartData.length > 0 ? (
            <div style={{ width: "100%", height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weddingsCreatedChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="period" tick={{ fill: "#475569", fontWeight: 700 }} />
                  <YAxis tick={{ fill: "#475569", fontWeight: 700 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1.5px solid #F8DBA0", boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                    formatter={(value, name) => [`${value} Weddings`, name]}
                  />
                  <Legend wrapperStyle={{ paddingTop: "10px", fontWeight: 700 }} />
                  {dynamicHostCreatedKeys.length > 0 ? (
                    dynamicHostCreatedKeys.map((planKey, idx) => (
                      <Bar
                        key={planKey}
                        dataKey={planKey}
                        name={`${planKey} Plan`}
                        fill={PACKAGE_BAR_COLORS[idx % PACKAGE_BAR_COLORS.length]}
                        radius={[6, 6, 0, 0]}
                        barSize={30}
                        minPointSize={6}
                      />
                    ))
                  ) : (
                    <>
                      <Bar dataKey="Essential" fill="#A60000" radius={[6, 6, 0, 0]} name="Essential Plan" minPointSize={6} />
                      <Bar dataKey="Premium" fill="#800001" radius={[6, 6, 0, 0]} name="Premium Plan" minPointSize={6} />
                      <Bar dataKey="Elite" fill="#059669" radius={[6, 6, 0, 0]} name="Elite Plan" minPointSize={6} />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-center flex-column py-5 my-3 bg-light rounded-3">
              <small className="text-muted fw-bold">No weddings created data available for selected period</small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Image Lightbox Modal */}
      {selectedImage && (
        <Modal show={Boolean(selectedImage)} onHide={() => setSelectedImage(null)} centered size="lg">
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="small fw-extrabold" style={{ color: "#A60000" }}>Image Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-4" style={{ backgroundColor: "#faf7f2" }}>
            <img
              src={selectedImage}
              alt="Portfolio enlarged preview"
              className="img-fluid rounded-3 shadow-lg"
              style={{ maxHeight: "75vh", objectFit: "contain", border: "3px solid #A60000" }}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Weddings Modal */}
      <Modal
        show={showWeddingsModal}
        onHide={() => setShowWeddingsModal(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-extrabold d-flex align-items-center gap-2" style={{ fontSize: "1.25rem", color: "#A60000" }}>
            <Calendar size={24} style={{ color: "#A60000" }} />
            Partner Weddings ({modalTotalHosts})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ backgroundColor: "#faf7f2" }}>
          {loadingModalHosts ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: "#A60000" }} />
            </div>
          ) : modalHosts && modalHosts.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table hover align="middle" className="border rounded-4 overflow-hidden shadow-sm bg-white">
                  <thead style={{ backgroundColor: "#A60000", color: "#101828" }}>
                    <tr>
                      <th className="py-3 px-3">S.No</th>
                      <th className="py-3 px-3">Couple & Host Name</th>
                      <th className="py-3 px-3">Phone Number</th>
                      <th className="py-3 px-3">Host Side</th>
                      <th className="py-3 px-3">Plan Name</th>
                      <th className="py-3 px-3">Created Date</th>
                      <th className="py-3 px-3">Guests</th>
                      <th className="py-3 px-3">Invites</th>
                      <th className="py-3 px-3">RSVPs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalHosts.map((host, idx) => {
                      const coupleImg = host.couplePhoto ? getImageUrl(host.couplePhoto) : null;
                      const coupleNames =
                        host.groomName && host.brideName
                          ? `${host.groomName} & ${host.brideName}`
                          : host.hostName || "N/A";
                      const formattedDate = host.createdAt
                        ? new Date(host.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                        : "-";

                      return (
                        <tr key={host._id || host.id || idx}>
                          <td className="px-3 fw-bold" style={{ color: "#0f172a" }}>{modalPage * 10 + idx + 1}</td>
                          <td className="px-3">
                            <div className="d-flex align-items-center gap-2">
                              {coupleImg ? (
                                <img
                                  src={coupleImg}
                                  alt={coupleNames}
                                  className="rounded-circle object-fit-cover"
                                  style={{ width: 38, height: 38, border: "2px solid #A60000" }}
                                />
                              ) : (
                                <div
                                  className="d-flex align-items-center justify-content-center rounded-circle fw-bold"
                                  style={{ width: 38, height: 38, fontSize: "0.85rem", backgroundColor: "#F8DBA0", color: "#A60000" }}
                                >
                                  {host.hostName ? host.hostName.charAt(0).toUpperCase() : "W"}
                                </div>
                              )}
                              <div>
                                <div className="fw-extrabold mb-0" style={{ fontSize: "0.88rem", color: "#0f172a" }}>
                                  {coupleNames}
                                </div>
                                <span className="fw-bold small" style={{ fontSize: "0.75rem", color: "#475569" }}>
                                  Host: {host.hostName || "-"}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 fw-bold" style={{ color: "#0f172a" }}>
                            {host.countryCode || "+91"} {host.phoneNumber || "-"}
                          </td>
                          <td className="px-3">
                            <Badge
                              bg={host.hostSide === "bride" ? "danger" : "primary"}
                              className="text-capitalize px-3 py-2 fw-bold"
                              style={{ borderRadius: "50rem" }}
                            >
                              {host.hostSide || "-"}
                            </Badge>
                          </td>
                          <td className="px-3">
                            <Badge className="px-3 py-2 fw-semibold">
                              {host.plan?.name || "-"}
                            </Badge>
                          </td>
                          <td className="px-3 small fw-bold" style={{ color: "#475569" }}>{formattedDate}</td>
                          <td className="px-3 fw-extrabold" style={{ color: "#0f172a" }}>{host.totalGuests ?? 0}</td>
                          <td className="px-3 fw-bold" style={{ color: "#475569" }}>{host.totalInvites ?? 0}</td>
                          <td className="px-3 fw-bold" style={{ color: "#047857" }}>{host.totalRsvps ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <Pagination
                pageCount={modalTotalPages}
                pageValue={modalPage}
                setPage={setModalPage}
                total={modalTotalHosts}
                pageSize={10}
                itemLabel="weddings"
              />
            </>
          ) : (
            <div className="text-center py-5">
              <Calendar size={48} className="mb-2 opacity-50" style={{ color: "#A60000" }} />
              <h6 className="fw-bold" style={{ color: "#A60000" }}>No Weddings Found</h6>
              <p className="small mb-0 fw-bold" style={{ color: "#475569" }}>This partner has no host weddings created yet.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: "#faf7f2" }}>
          <Button variant="secondary" className="rounded-pill px-4 fw-bold" onClick={() => setShowWeddingsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for Editing Message Limit */}
      <Modal show={showLimitModal} onHide={() => setShowLimitModal(false)} centered size="sm">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-extrabold h6 mb-0" style={{ color: "#A60000" }}>
            Edit Message Limit
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4 px-4" style={{ backgroundColor: "#ffffff" }}>
          <div className="mb-3">
            <label className="form-label fw-extrabold small" style={{ color: "#A60000" }}>
              New Message Limit <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              min={partnerData?.plan?.whatsappMessageLimit ?? 0}
              className="form-control fw-extrabold"
              placeholder="Enter message limit"
              value={newLimitValue}
              onChange={(e) => setNewLimitValue(e.target.value)}
              style={{ borderRadius: "0.75rem", borderColor: "#A60000", padding: "0.6rem 1rem", color: "#0f172a" }}
            />
            <small className="mt-2 d-block fw-bold" style={{ fontSize: "0.75rem", color: "#475569" }}>
              Current limit: {partnerData?.plan?.whatsappMessageLimit ?? 0} (Cannot be less than existing limit)
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4" style={{ backgroundColor: "#ffffff" }}>
          <Button variant="light" size="sm" className="rounded-pill px-3 fw-bold" onClick={() => setShowLimitModal(false)} disabled={savingLimit}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="btn-primary qx-btn-brand px-4"
            onClick={handleSaveMessageLimit}
            disabled={savingLimit}
          >
            {savingLimit ? <Spinner animation="border" size="sm" /> : "Save Limit"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PartnerDetail;